import { HEIC_FILE_EXTENSIONS } from '@brighte/validation';

// Surfaced to the customer verbatim when neither decode path works, so they know to retry or
// pick a different format rather than seeing the generic upload failure.
export const HEIC_CONVERSION_ERROR_MESSAGE =
  'We couldn’t process that photo. Please try uploading it again, or save it as a JPEG or PDF first.';

const JPEG_MIME_TYPE = 'image/jpeg';
const JPEG_QUALITY = 0.9;
const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

// ISO-BMFF brands written by iOS cameras. The layout is: 4-byte box size, the literal 'ftyp',
// then the 4-byte major brand.
const FTYP_OFFSET = 4;
const BRAND_OFFSET = 8;
const HEADER_BYTES = 12;
const HEIC_BRANDS = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'mif1', 'msf1'];

export class HeicConversionError extends Error {
  constructor(cause?: unknown) {
    super(HEIC_CONVERSION_ERROR_MESSAGE);
    this.name = 'HeicConversionError';
    this.cause = cause;
  }
}

function readAscii(header: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...Array.from(header.subarray(start, end)));
}

async function hasHeicSignature(file: File): Promise<boolean> {
  try {
    const header = new Uint8Array(await file.slice(0, HEADER_BYTES).arrayBuffer());
    if (header.length < HEADER_BYTES) {
      return false;
    }
    return (
      readAscii(header, FTYP_OFFSET, BRAND_OFFSET) === 'ftyp' &&
      HEIC_BRANDS.includes(readAscii(header, BRAND_OFFSET, HEADER_BYTES))
    );
  } catch {
    return false;
  }
}

/**
 * Browsers are inconsistent about HEIC: the mime type is often an empty string, and a file shared
 * from another device can arrive with no extension at all. Check the name and mime type first
 * (cheap, no read) and only fall back to sniffing the container signature.
 */
export async function isHeicFile(file: File): Promise<boolean> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension && HEIC_FILE_EXTENSIONS.includes(extension)) {
    return true;
  }
  if (HEIC_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }
  return hasHeicSignature(file);
}

export function toJpegFilename(filename: string): string {
  if (/\.jpe?g$/i.test(filename)) {
    return filename;
  }
  const withoutHeicExtension = filename.replace(/\.(heic|heif)$/i, '');
  return `${withoutHeicExtension}.jpg`;
}

/**
 * Safari/WebKit decodes HEIC natively because the codec ships with iOS and macOS — which covers
 * the iPhone customer this exists for, at no bundle cost. Every other browser throws here and
 * falls through to the wasm decoder.
 */
async function convertUsingBrowserCodec(file: File): Promise<Blob | undefined> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return undefined;
  }

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const context = canvas.getContext('2d');
    if (!context) {
      return undefined;
    }
    context.drawImage(bitmap, 0, 0);

    return await new Promise<Blob | undefined>(resolve => {
      canvas.toBlob(blob => resolve(blob ?? undefined), JPEG_MIME_TYPE, JPEG_QUALITY);
    });
  } catch {
    return undefined;
  } finally {
    bitmap?.close();
  }
}

// libheif wasm (LGPL-3.0) — a lazy chunk, fetched only once a customer actually picks
// a HEIC on a browser that cannot decode one itself. It decodes in a Web Worker, off the main thread.
async function convertUsingLibheif(file: File): Promise<Blob> {
  const { heicTo } = await import('heic-to');
  return heicTo({ blob: file, type: JPEG_MIME_TYPE, quality: JPEG_QUALITY });
}

/**
 * Transcodes a HEIC/HEIF file to JPEG and renames it to match. Everything downstream — the
 * server-side extension allowlist, the S3 key, Bedrock and Textract — only ever sees the JPEG.
 *
 * Throws HeicConversionError when both decode paths fail. Failing closed is deliberate: an
 * unconverted .heic is rejected by the backend with a far less useful message.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  let converted: Blob | undefined;
  try {
    converted = (await convertUsingBrowserCodec(file)) ?? (await convertUsingLibheif(file));
  } catch (error) {
    throw new HeicConversionError(error);
  }

  if (!converted || converted.size === 0) {
    throw new HeicConversionError();
  }

  return new File([converted], toJpegFilename(file.name), {
    type: JPEG_MIME_TYPE,
    lastModified: file.lastModified,
  });
}
